import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, Trash2, Edit, Link2, Award } from 'lucide-react';
import CountryFlag from '../common/CountryFlag';
import { useSelector, useDispatch } from 'react-redux';
import { doctorProfileUpdate, UpdateDoctor } from '../../redux/doctorSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import emptyedu from '../../assets/img/colorkit (3).svg';
import emptylink from '../../assets/img/colorkit (2).svg';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import Cropper from 'react-easy-crop';
import { removeDoctorAdminProfileImage } from '../../redux/doctorSlice.js';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { CheckCircle, XCircle, Loader2, Send, RefreshCw, Check } from 'lucide-react';
import ProfessionSelect from '../common/ProfessionSelect';
import { getFileURL } from '../../utils/fileURL';
import GenderSelect from '../common/GenderSelect';
import { getAllProfessionsList, docPatCreationCountryList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';

// Per-row translated profession name. Profession entries may be objects {professionId,
// professionName} or bare ID strings (legacy). Resolves from the master translation cache.
const ProfessionName = ({ profession, professionListAll }) => {
    const id = typeof profession === 'object' ? (profession?.professionId || profession?._id) : profession;
    const fallback = typeof profession === 'object'
        ? (profession?.professionName || '')
        : (professionListAll?.find(pr => pr.professionId === profession)?.professionName || profession || '');
    const label = useMasterLabel('masterProfession', id, 'professionName', fallback);
    return <>{label}</>;
};

const formatDobDisplay = (dob) => {
    if (!dob) return '-';
    let date;
    const str = String(dob).trim();
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) date = new Date(str);
    else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) { const p = str.split(/[/-]/); date = new Date(`${p[2]}-${p[1]}-${p[0]}`); }
    else date = new Date(str);
    return isNaN(date?.getTime()) ? '-' : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const DoctorProfileTab = ({ doctor }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef();
    const { t } = useTranslation('doctorprofiletab');
    const { profileImage, loading, error, selectedDoctor, removeloading } = useSelector((state) => state.doctor);
    const { trynavigate } = useSelector((state) => state.blocking);
    const { professionListAll, professionListAllLoading, countryTimezoneList } = useSelector((state) => state.master);
    const userRole = localStorage.getItem('Role');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isIncompleteMode, setIsIncompleteMode] = useState(false);
    const [incompleteFields, setIncompleteFields] = useState([]);
    const [formData, setFormData] = useState({
        name: doctor.name || '',
        surname: doctor.surname || '',
        gender: doctor.gender || '',
        dob: doctor.dob ? doctor.dob.split('T')[0] : '',
        email: doctor.email || '',
        phone: doctor.phone || '',
        altPhone: doctor.altPhone || '',
        licenseNumber: doctor.licenseNumber || '',
        description: doctor.description || '',
        profession: Array.isArray(doctor.profession) ? doctor.profession.map(p => p.professionId || p) : (doctor.profession?.professionId ? [doctor.profession.professionId] : (doctor.profession ? [doctor.profession] : [])),
        countryId: doctor.country?.countryId || doctor.country || '',
        timeZone: doctor.timeZone || '',
    });
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;

    // Fetch professions list for dropdown (not needed for patient view-only mode)
    useEffect(() => {
        if (userRole !== 'patient') {
            if (!professionListAll?.length) dispatch(getAllProfessionsList());
            if (!countryTimezoneList?.length) dispatch(docPatCreationCountryList());
        }
    }, [dispatch, userRole]);

    const getCountryName = (country) => {
        if (!country) return '';
        if (typeof country === 'object') return country.countryName || '';
        return country;
    };

    const [errors, setErrors] = useState({});
    const [isLoadingPersonalInfo, setIsLoadingPersonalInfo] = useState(false);

    const axiosInstance = useAxiosAuth();
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth || {});

    // Phone logic states
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress

    // Phone OTP states
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // Email logic states
    const [emailStatus, setEmailStatus] = useState(null);
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef('');
    const isEmailCheckInProgressRef = useRef(false);

    // Email OTP states
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // OTP related functions
    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('doctorProfile.failed_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('doctorProfile.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('doctorProfile.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('doctorProfile.failed_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('doctorProfile.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('doctorProfile.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    // Cleanup OTP timers on unmount
    useEffect(() => {
        return () => {
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
            if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);
        };
    }, []);

    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check
    useEffect(() => {
        if (!isEditMode) return;

        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';
        const originalEmail = selectedDoctor?.email || doctor?.email || '';

        if (originalEmail && email === originalEmail) {
            setEmailStatus(true);
            setEmailOtpVerified(true);
            lastCheckedEmailRef.current = email;
            return;
        }

        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            setEmailOtpVerified(false);
            lastCheckedEmailRef.current = '';
            return;
        }

        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        setEmailStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setEmailOtpVerified(false);
                        }
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = '';
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.email, isEditMode, selectedDoctor?.email, doctor?.email]);

    // Debounced phone check
    useEffect(() => {
        if (!isEditMode) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';
        const originalPhone = selectedDoctor?.phone || doctor?.phone || '';

        // Treat original phone as valid
        if (originalPhone && phone === originalPhone) {
            setPhoneStatus(true);
            setPhoneOtpVerified(true);
            lastCheckedPhoneRef.current = phone;
            return;
        }

        const isValid = /^\d+$/.test(phone) && phone.length >= (selectedCountry?.mobileMinLength || 9) && phone.length <= (selectedCountry?.mobileMaxLength || 10);

        if (!phone || !isValid) {
            setPhoneStatus(null);
            setPhoneOtpVerified(false);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();
            if (!trimmedPhone || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setPhoneOtpVerified(false);
                        }
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.phone, formData.countryId, isEditMode, selectedDoctor?.phone, doctor?.phone]);
    const [charLimitReached, setCharLimitReached] = useState({});
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isLinkModalEdit, setIsLinkModalEdit] = useState(false);
    const [links, setLinks] = useState(
        doctor.websiteLink?.map((link, index) =>
            typeof link === 'string'
                ? { id: index + 1, url: link }
                : { id: index + 1, url: link.url }
        ) || []
    );
    const [newLink, setNewLink] = useState({ url: '' });
    const [isLoadingLink, setIsLoadingLink] = useState(false);
    const [isEducationModalOpen, setIsEducationModalOpen] = useState(false);
    const [isEducationModalEdit, setIsEducationModalEdit] = useState(false);
    const [education, setEducation] = useState(
        doctor.certification?.map((cert, index) => ({
            id: index + 1,
            title: cert.title || '',
            year: cert.date ? new Date(cert.date).getUTCFullYear().toString() : '',
            location: cert.location || '',
            institute: cert.institute || '',
            description: cert.description || '',
        })) || []
    );
    const [newEducation, setNewEducation] = useState({
        title: '',
        year: '',
        location: '',
        institute: '',
        description: '',
    });
    const [isLoadingEducation, setIsLoadingEducation] = useState(false);

    // === Profile Image Modal States ===
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Check profile status and identify incomplete fields
    useEffect(() => {
        if (selectedDoctor && selectedDoctor?.doctorId === doctor.doctorId) {
            // Check if profile is incomplete
            const isIncomplete = selectedDoctor.profileStatus === 'incomplete';
            setIsIncompleteMode(isIncomplete);

            if (isIncomplete) {
                // Identify which required fields are missing
                const missing = [];
                if (!selectedDoctor.phone || selectedDoctor.phone.trim() === '') missing.push('phone');

                setIncompleteFields(missing);

                // Automatically enable edit mode if there are incomplete fields
                if (missing.length > 0) {
                    setIsEditMode(true);
                    // dispatch(restrictnavigate(true));
                }
            } else {
                setIncompleteFields([]);
            }
        }
    }, [selectedDoctor, doctor.doctorId, dispatch]);

    // Fetch updated doctor data
    useEffect(() => {
        if (selectedDoctor && selectedDoctor?.doctorId === doctor.doctorId) {
            const matchedCountry = countryTimezoneList?.find(c =>
                c.countryName === selectedDoctor.country ||
                c.countryId === selectedDoctor.country?.countryId ||
                c.countryId === selectedDoctor.country ||
                c.countryId === selectedDoctor.countryId
            );
            setFormData({
                name: selectedDoctor.name || '',
                surname: selectedDoctor.surname || '',
                gender: selectedDoctor.gender || '',
                dob: selectedDoctor.dob ? selectedDoctor.dob.split('T')[0] : '',
                email: selectedDoctor.email || '',
                phone: selectedDoctor.phone || '',
                altPhone: selectedDoctor.altPhone || '',
                licenseNumber: selectedDoctor.licenseNumber || '',
                description: selectedDoctor.description || '',
                profession: Array.isArray(selectedDoctor.profession) ? selectedDoctor.profession.map(p => p.professionId || p) : (selectedDoctor.profession?.professionId ? [selectedDoctor.profession.professionId] : (selectedDoctor.profession ? [selectedDoctor.profession] : [])),
                countryId: matchedCountry?.countryId || selectedDoctor.country?.countryId || '',
                timeZone: selectedDoctor.timeZone || '',
            });
            setLinks(
                selectedDoctor.websiteLink?.map((link, index) =>
                    typeof link === 'string'
                        ? { id: index + 1, url: link }
                        : { id: index + 1, url: link.url }
                ) || []
            );
            setEducation(
                selectedDoctor.certification?.map((cert, index) => ({
                    id: index + 1,
                    title: cert.title || '',
                    year: cert.date ? new Date(cert.date).getUTCFullYear().toString() : '',
                    location: cert.location || '',
                    institute: cert.institute || '',
                    description: cert.description || '',
                })) || []
            );
        }
    }, [selectedDoctor, doctor.doctorId, countryTimezoneList]);

    const profileOwner = userRole === 'doctor' ? t('doctorprofiletab.your') : t('doctorprofiletab.doctor');

    // Navigation blocking
    useEffect(() => {
        if (trynavigate && isEditMode) {
            Swal.fire({
                title: t('doctorprofiletab.editing_in_progress'),
                text: t('doctorprofiletab.editing_in_progress_text', { profileOwner }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('doctorprofiletab.abort'),
                confirmButtonColor: '#d33',
                cancelButtonText: t('doctorprofiletab.cancel'),
                cancelButtonColor: '#9ca3af',
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    // Discard — leave pendingNavigation alone so the parent
                    // consumer (Sidebar / DoctorProfile) finishes the route /
                    // tab navigation the user originally clicked.
                    dispatch(tryingnavigate(false));
                    handleCancelEdit();
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    // Stay — drop the captured destination.
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        }
    }, [trynavigate, dispatch, t, isEditMode]);

    // === Profile Image Modal Functions ===
    const handleEditClick = () => {
        setIsModalOpen(true);
        setSelectedFile(null);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setIsCropping(false);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.warn(t('doctorprofiletab.invalidfiletype'));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result);
            setSelectedFile(file);
            setIsCropping(true);
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = new Image();
        image.src = imageSrc;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg');
        });
    };

    const handleSubmitCrop = async () => {
        if (!croppedAreaPixels || !imageSrc) return;

        setIsUploading(true);
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const file = new File([croppedBlob], selectedFile.name, { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append('file', file);

            await dispatch(doctorProfileUpdate({ docid: doctor.doctorId, formData })).unwrap();

            // Update UI with cropped image
            const newImageUrl = URL.createObjectURL(croppedBlob);
            // You may want to update Redux state or local state here

            toast.success(t('doctorprofiletab.upload_success_text'));

            setIsModalOpen(false);
        } catch (err) {
            toast.warn(t('doctorprofiletab.upload_error_text'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = () => {
        Swal.fire({
            title: t('doctorprofiletab.remove_image_title'),
            text: t('doctorprofiletab.remove_image_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('doctorprofiletab.remove'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonText: t('doctorprofiletab.cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(removeDoctorAdminProfileImage({ role: 'doctor', doctorId: doctor.doctorId }))
                    .unwrap()
                    .then(() => {
                        toast.success(t('doctorprofiletab.removed_success_text'));
                    })
                    .catch(() => {
                        toast.error(t('doctorprofiletab.remove_error_text'));
                    });
            }
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setImageSrc(null);
        setSelectedFile(null);
        setIsCropping(false);
    };

    // === Rest of your existing functions (unchanged) ===
    // Check if all required fields are filled (for incomplete mode)
    const areAllRequiredFieldsFilled = () => {
        if (!isIncompleteMode) return true;

        return (
            formData.surname?.trim() !== '' &&
            formData.gender?.trim() !== '' &&
            formData.dob?.trim() !== '' &&
            formData.phone?.trim() !== ''
        );
    };

    const calculateAge = (dob) => {
        if (!dob) return 0;
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = t('doctorprofiletab.nameisrequired');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('doctorprofiletab.nameisrequired');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.name.trim())) {
            newErrors.name = t('doctorProfile.name_invalid_chars');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('doctorProfile.name_min_2_chars');
        } else if (formData.name.trim().length > 25) {
            newErrors.name = t('doctorprofiletab.nameexceed');
        }

        // Surname validation
        if (!formData.surname.trim()) {
            newErrors.surname = t('doctorprofiletab.surnameisrequired');
        } else if (/^\s+$/.test(formData.surname)) {
            newErrors.surname = t('doctorprofiletab.surnameisrequired');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.surname.trim())) {
            newErrors.surname = t('doctorProfile.surname_invalid_chars');
        } else if (formData.surname.trim().length < 2) {
            newErrors.surname = t('doctorProfile.surname_min_2_chars');
        } else if (formData.surname.trim().length > 25) {
            newErrors.surname = t('doctorprofiletab.surnameexceed');
        }

        // Dynamic country validation rules
        const phoneMin = selectedCountry?.mobileMinLength || 9;
        const phoneMax = selectedCountry?.mobileMaxLength || 10;
        const altMin = selectedCountry?.altMinLength || phoneMin;
        const altMax = selectedCountry?.altMaxLength || phoneMax;

        // Profession validation
        if (!formData.profession || formData.profession.length === 0) {
            newErrors.profession = t('doctorprofiletab.profession_required', 'Please select at least 1 profession');
        } else if (formData.profession.length > 3) {
            newErrors.profession = t('doctorprofiletab.profession_max_exceeded', 'Maximum 3 professions allowed');
        }

        // Additional validation for incomplete mode
        if (isIncompleteMode) {
            if (!formData.gender?.trim()) {
                newErrors.gender = t('doctorprofiletab.genderisrequired');
            }

            // DOB validation
            if (!formData.dob?.trim()) {
                newErrors.dob = t('doctorprofiletab.dobisrequired');
            } else {
                const dobDate = new Date(formData.dob);
                if (dobDate > new Date()) {
                    newErrors.dob = t('doctorProfile.dob_not_future');
                } else if (calculateAge(formData.dob) < 18) {
                    newErrors.dob = t('doctorProfile.must_be_18');
                } else if (dobDate.getFullYear() < 1900) {
                    newErrors.dob = t('doctorProfile.dob_after_1900');
                }
            }

            // Email validation (when changed)
            if (formData.email && formData.email.trim() && (selectedDoctor?.email || doctor?.email) !== formData.email.trim()) {
                const emailVal = formData.email.trim();
                const emailRegexLocal = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegexLocal.test(emailVal)) {
                    newErrors.email = t('doctorProfile.invalid_email_format');
                } else if (emailStatus === false) {
                    newErrors.email = t('doctorProfile.email_already_exists');
                } else if (emailStatus === null && !emailCheckLoading) {
                    newErrors.email = t('doctorProfile.please_verify_email');
                } else if (emailStatus === true && !emailOtpVerified) {
                    newErrors.email = t('doctorProfile.verify_email_otp');
                }
            }

            if (!formData.phone?.trim()) {
                newErrors.phone = t('doctorprofiletab.phoneisrequired');
            } else {
                // Country-specific phone validation
                const phoneRegex = new RegExp(`^\\d{${phoneMin},${phoneMax}}$`);
                if (!phoneRegex.test(formData.phone)) {
                    newErrors.phone = t('doctorprofiletab.error_phone_invalid');
                }

                if (!newErrors.phone) {
                    if (phoneStatus === false) {
                        newErrors.phone = t('doctorProfile.phone_already_exists');
                    } else if (phoneStatus === null && !mobileCheckLoading) {
                        newErrors.phone = t('doctorProfile.please_verify_phone');
                    } else if (phoneStatus === true && !phoneOtpVerified) {
                        newErrors.phone = t('doctorProfile.verify_phone_otp');
                    }
                }
            }

            // Alt Phone validation (optional)
            if (formData.altPhone && formData.altPhone.trim()) {
                const altRegex = new RegExp(`^\\d{${altMin},${altMax}}$`);
                if (!altRegex.test(formData.altPhone)) {
                    newErrors.altPhone = t('doctorprofiletab.error_phone_invalid');
                }
            }

            // License Number validation (optional in incomplete mode now)
            if (formData.licenseNumber?.trim()) {
                if (formData.licenseNumber.trim().length < 3) {
                    newErrors.licenseNumber = t('doctorProfile.license_min_3_chars');
                } else if (formData.licenseNumber.trim().length > 20) {
                    newErrors.licenseNumber = t('doctorProfile.license_max_20_chars');
                } else if (!/^[a-zA-Z0-9]+$/.test(formData.licenseNumber.trim())) {
                    newErrors.licenseNumber = t('doctorProfile.license_alphanumeric_only');
                }
            }

        } else {
            // Email validation (when changed)
            if (formData.email && formData.email.trim() && (selectedDoctor?.email || doctor?.email) !== formData.email.trim()) {
                const emailVal = formData.email.trim();
                const emailRegexLocal = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegexLocal.test(emailVal)) {
                    newErrors.email = t('doctorProfile.invalid_email_format');
                } else if (emailStatus === false) {
                    newErrors.email = t('doctorProfile.email_already_exists');
                } else if (emailStatus === null && !emailCheckLoading) {
                    newErrors.email = t('doctorProfile.please_verify_email');
                } else if (emailStatus === true && !emailOtpVerified) {
                    newErrors.email = t('doctorProfile.verify_email_otp');
                }
            }

            // Normal mode - validate phone
            const isPhoneRequired = userRole === 'admin' || userRole === 'Admin';
            if (isPhoneRequired && (!formData.phone || !formData.phone.trim())) {
                newErrors.phone = t('doctorprofiletab.phoneisrequired');
            } else if (formData.phone && formData.phone.trim()) {
                const phoneRegex = new RegExp(`^\\d{${phoneMin},${phoneMax}}$`);
                if (!phoneRegex.test(formData.phone)) {
                    newErrors.phone = t('doctorprofiletab.error_phone_invalid');
                }

                if (!newErrors.phone) {
                    if (phoneStatus === false) {
                        newErrors.phone = t('doctorProfile.phone_already_exists');
                    } else if (phoneStatus === null && !mobileCheckLoading) {
                        newErrors.phone = t('doctorProfile.please_verify_phone');
                    } else if (phoneStatus === true && !phoneOtpVerified) {
                        newErrors.phone = t('doctorProfile.verify_phone_otp');
                    }
                }
            }

            // Alt Phone validation (optional)
            if (formData.altPhone && formData.altPhone.trim()) {
                const altRegex = new RegExp(`^\\d{${altMin},${altMax}}$`);
                if (!altRegex.test(formData.altPhone)) {
                    newErrors.altPhone = t('doctorprofiletab.error_phone_invalid');
                }
            }

            // License Number validation (if provided in normal mode)
            if (formData.licenseNumber && formData.licenseNumber.trim()) {
                if (formData.licenseNumber.trim().length < 3) {
                    newErrors.licenseNumber = t('doctorProfile.license_min_3_chars');
                } else if (formData.licenseNumber.trim().length > 20) {
                    newErrors.licenseNumber = t('doctorProfile.license_max_20_chars');
                } else if (!/^[a-zA-Z0-9]+$/.test(formData.licenseNumber.trim())) {
                    newErrors.licenseNumber = t('doctorProfile.license_alphanumeric_only');
                }
            }
        }

        if (formData.description) {
            // Sanitize description - remove dangerous characters
            const sanitizedDescription = formData.description.replace(/[<>]/g, '');
            if (sanitizedDescription.length > 100) {
                newErrors.description = t('doctorprofiletab.descriptionwarn');
            } else if (/^\s+$/.test(formData.description)) {
                newErrors.description = t('doctorProfile.description_only_whitespace');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateLink = () => {
        const newErrors = {};
        if (!newLink.url.trim()) {
            newErrors.url = t('doctorprofiletab.urlrequired');
        } else if (/^\s+$/.test(newLink.url)) {
            newErrors.url = t('doctorprofiletab.urlrequired');
        } else {
            // Sanitize URL - remove dangerous characters
            let sanitizedUrl = newLink.url.replace(/[<>]/g, '').trim();
            // Auto-prepend https:// if no protocol is provided
            if (!/^https?:\/\//i.test(sanitizedUrl)) {
                sanitizedUrl = 'https://' + sanitizedUrl;
                setNewLink({ ...newLink, url: sanitizedUrl });
            }
            if (!/^https?:\/\/[^\s$.?#].[^\s]*$/.test(sanitizedUrl)) {
                newErrors.url = t('doctorprofiletab.invalidurl');
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateEducation = () => {
        const newErrors = {};
        if (!newEducation.title.trim()) {
            newErrors.title = t('doctorprofiletab.titlerequired');
        } else if (/^\s+$/.test(newEducation.title)) {
            newErrors.title = t('doctorprofiletab.titlerequired');
        } else if (newEducation.title.length > 100) {
            newErrors.title = t('doctorprofiletab.titleexceed');
        }
        if (!newEducation.year) {
            newErrors.year = t('doctorprofiletab.yearrequired');
        }
        if (!newEducation.location.trim()) {
            newErrors.location = t('doctorprofiletab.locationrequired');
        } else if (/^\s+$/.test(newEducation.location)) {
            newErrors.location = t('doctorprofiletab.locationrequired');
        } else if (newEducation.location.length > 100) {
            newErrors.location = t('doctorprofiletab.locationexceed');
        }
        if (!newEducation.institute.trim()) {
            newErrors.institute = t('doctorprofiletab.instituterequired');
        } else if (/^\s+$/.test(newEducation.institute)) {
            newErrors.institute = t('doctorprofiletab.instituterequired');
        } else if (newEducation.institute.length > 100) {
            newErrors.institute = t('doctorprofiletab.instituteexceed');
        }
        if (newEducation.description) {
            // Sanitize description - remove dangerous characters
            const sanitizedDescription = newEducation.description.replace(/[<>]/g, '');
            if (sanitizedDescription.length > 150) {
                newErrors.description = t('doctorprofiletab.descriptionwarn');
            } else if (/^\s+$/.test(newEducation.description)) {
                newErrors.description = t('doctorProfile.description_only_whitespace');
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = { name: 25, surname: 25, licenseNumber: 20, description: 100 };
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): always uppercase.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Sanitize description - remove dangerous characters
        if (name === 'description') {
            processedValue = processedValue.replace(/[<>]/g, '');
        }

        // Restrict phone and altPhone to digits only
        if ((name === "phone" || name === "altPhone") && processedValue && !/^\d*$/.test(processedValue)) return;

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === "name" || name === "surname") && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) return;

        // Restrict licenseNumber to alphanumeric only (no special characters)
        if (name === "licenseNumber" && processedValue && !/^[a-zA-Z0-9]*$/.test(processedValue)) return;

        // Phone length restriction based on country
        if (name === 'phone' || name === 'altPhone') {
            const maxPhoneLength = selectedCountry?.mobileMaxLength || 10;
            if (processedValue.length > maxPhoneLength) return;
        }

        // Check if character limit is reached
        if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }

        // Apply max length restrictions
        if (maxLengths[name] && processedValue.length > maxLengths[name]) {
            return; // Don't update if exceeds max length
        }

        setFormData({ ...formData, [name]: processedValue });

        // Instant DOB validation in incomplete mode — surfaces 18+/future/range errors
        // as soon as the user picks a date, not just on submit.
        if (name === 'dob' && isIncompleteMode) {
            let dobErr = '';
            if (processedValue) {
                const dobDate = new Date(processedValue);
                if (isNaN(dobDate.getTime())) {
                    dobErr = t('doctorProfile.invalid_date_format', { defaultValue: 'Invalid date format' });
                } else if (dobDate > new Date()) {
                    dobErr = t('doctorProfile.dob_not_future');
                } else if (dobDate.getFullYear() < 1900) {
                    dobErr = t('doctorProfile.dob_after_1900');
                } else if (calculateAge(processedValue) < 18) {
                    dobErr = t('doctorProfile.must_be_18');
                }
            }
            setErrors((prev) => ({ ...prev, dob: dobErr }));
            return;
        }

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleLinkInputChange = (e) => {
        const { name, value } = e.target;
        // Sanitize URL - remove dangerous characters
        const sanitizedValue = value.replace(/[<>]/g, '');
        setNewLink({ ...newLink, [name]: sanitizedValue });
    };

    const handleEducationInputChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = { title: 100, location: 100, institute: 100, description: 150 };
        let processedValue = value;

        // Sanitize description - remove dangerous characters
        if (name === 'description') {
            processedValue = value.replace(/[<>]/g, '');
        }

        if (name === 'title' || name === 'location' || name === 'institute' || name === 'description') {
            const textOnlyPattern = /^[a-zA-ZÀ-ÿ\s.,'-]*$/;
            if (textOnlyPattern.test(processedValue)) {
                // Auto uppercase for title field
                if (name === 'title') processedValue = processedValue.toUpperCase();
                setNewEducation({ ...newEducation, [name]: processedValue });
                if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
                    setCharLimitReached({ ...charLimitReached, [name]: true });
                } else {
                    setCharLimitReached({ ...charLimitReached, [name]: false });
                }
            }
        } else {
            setNewEducation({ ...newEducation, [name]: processedValue });
        }
    };

    const handleSubmitPersonalInfo = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const result = await Swal.fire({
                title: t('doctorprofiletab.confirm_update_title'),
                text: t('doctorprofiletab.confirm_update_text'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('doctorprofiletab.yes_update'),
            });
            if (result.isConfirmed) {
                setIsLoadingPersonalInfo(true);
                try {
                    // Prepare data to send
                    const dataToSend = { ...formData };

                    // If in incomplete mode and all fields are filled, update profileStatus to "Completed"
                    if (isIncompleteMode && areAllRequiredFieldsFilled()) {
                        dataToSend.profileStatus = 'complete';
                    }

                    if (emailOtpVerified) dataToSend.emailVerified = true;
                    if (phoneOtpVerified) dataToSend.phoneNoVerified = true;

                    await dispatch(UpdateDoctor({
                        docid: doctor.doctorId,
                        editedData: dataToSend,
                        passkey: 'PersonalInfo',
                    })).unwrap();
                    toast.success(t('doctorprofiletab.update_success_text'));
                    dispatch(restrictnavigate(false));
                    setIsEditMode(false);

                    // Switch from incomplete mode to normal mode
                    if (isIncompleteMode && areAllRequiredFieldsFilled()) {
                        setIsIncompleteMode(false);
                        setIncompleteFields([]);
                    }
                } catch (err) {
                    toast.warn(t('doctorprofiletab.update_error_text'));
                } finally {
                    setIsLoadingPersonalInfo(false);
                }
            }
        }
    };

    const handleAddOrUpdateLink = async (e) => {
        e.preventDefault();
        if (validateLink()) {
            const result = await Swal.fire({
                title: t('doctorprofiletab.confirm_link_title'),
                text: isLinkModalEdit
                    ? t('doctorprofiletab.confirm_update_link_text')
                    : t('doctorprofiletab.confirm_add_link_text'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('doctorprofiletab.yes_save'),
            });
            if (!result.isConfirmed) return;
            setIsLoadingLink(true);
            const updatedLinks = isLinkModalEdit
                ? links.map((link) => (link.id === newLink.id ? newLink : link))
                : [...links, { id: links.length + 1, ...newLink }];
            try {
                await dispatch(UpdateDoctor({
                    docid: doctor.doctorId,
                    editedData: { websiteLink: updatedLinks.map((link) => ({ url: link.url })) },
                    passkey: 'UpdateLink',
                })).unwrap();
                setLinks(updatedLinks);
                setNewLink({ url: '' });
                setIsLinkModalOpen(false);
                setIsLinkModalEdit(false);
                toast.success(isLinkModalEdit
                        ? t('doctorprofiletab.link_update_success_text')
                        : t('doctorprofiletab.link_add_success_text'));
            } catch (err) {
                toast.warn(t('doctorprofiletab.link_error_text'));
            } finally {
                setIsLoadingLink(false);
            }
        }
    };

    const handleDeleteLink = async (id) => {
        const result = await Swal.fire({
            title: t('doctorprofiletab.delete_confirm_title'),
            text: t('doctorprofiletab.delete_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            cancelButtonText: t('doctorprofiletab.cancel'),
            confirmButtonText: t('doctorprofiletab.yesdelete'),
        });
        if (result.isConfirmed) {
            const updatedLinks = links.filter((link) => link.id !== id);
            try {
                await dispatch(UpdateDoctor({
                    docid: doctor.doctorId,
                    editedData: { websiteLink: updatedLinks.map((link) => ({ url: link.url })) },
                    passkey: 'DeleteLink',
                })).unwrap();
                setLinks(updatedLinks);
                toast.success(t('doctorprofiletab.deleted_success_text'));
            } catch (err) {
                toast.warn(t('doctorprofiletab.error_delete_text'));
            }
        }
    };

    const handleAddOrUpdateEducation = async (e) => {
        e.preventDefault();
        if (validateEducation()) {
            const result = await Swal.fire({
                title: t('doctorprofiletab.confirm_edu_title'),
                text: isEducationModalEdit
                    ? t('doctorprofiletab.confirm_update_edu_text')
                    : t('doctorprofiletab.confirm_add_edu_text'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('doctorprofiletab.yes_save'),
            });
            if (result.isConfirmed) {
                setIsLoadingEducation(true);
                const updatedEducation = isEducationModalEdit
                    ? education.map((edu) => (edu.id === newEducation.id ? newEducation : edu))
                    : [...education, { id: education.length + 1, ...newEducation }];
                try {
                    await dispatch(UpdateDoctor({
                        docid: doctor.doctorId,
                        editedData: {
                            certification: updatedEducation.map((edu) => ({
                                title: edu.title,
                                date: new Date(edu.year).toISOString(),
                                location: edu.location,
                                institute: edu.institute,
                                description: edu.description,
                            })),
                        },
                        passkey: 'UpdateEdu',
                    })).unwrap();
                    setEducation(updatedEducation);
                    setNewEducation({ title: '', year: '', location: '', institute: '', description: '' });
                    setIsEducationModalOpen(false);
                    setIsEducationModalEdit(false);
                    toast.success(isEducationModalEdit
                            ? t('doctorprofiletab.edu_update_success_text')
                            : t('doctorprofiletab.edu_add_success_text'));
                } catch (err) {
                    toast.warn(t('doctorprofiletab.edu_error_text'));
                } finally {
                    setIsLoadingEducation(false);
                }
            }
        }
    };

    const handleDeleteEducation = async (id) => {
        const result = await Swal.fire({
            title: t('doctorprofiletab.delete_confirm_title'),
            text: t('doctorprofiletab.delete_edu_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            cancelButtonText: t('doctorprofiletab.cancel'),
            confirmButtonText: t('doctorprofiletab.yesdelete'),
        });
        if (result.isConfirmed) {
            const updatedEducation = education.filter((edu) => edu.id !== id);
            try {
                await dispatch(UpdateDoctor({
                    docid: doctor.doctorId,
                    editedData: {
                        certification: updatedEducation.map((edu) => ({
                            title: edu.title,
                            date: new Date(edu.year).toISOString(),
                            location: edu.location,
                            institute: edu.institute,
                            description: edu.description,
                        })),
                    },
                    passkey: 'DeleteEdu',
                })).unwrap();
                setEducation(updatedEducation);
                toast.success(t('doctorprofiletab.deleted_success_text'));
            } catch (err) {
                toast.warn(t('doctorprofiletab.error_delete_text'));
            }
        }
    };

    const years = Array.from({ length: 50 }, (_, i) => (new Date().getFullYear() - i).toString());
    const handlePersonalInfoEdit = () => {
        setIsEditMode((prev) => !prev);
        dispatch(restrictnavigate(true));
    };
    const handleCancelEdit = () => {
        setIsEditMode(false);
        dispatch(restrictnavigate(false));
        setCharLimitReached({});
        setErrors({});
        setPhoneStatus(null);
        resetOtpPhoneState();
        lastCheckedPhoneRef.current = '';
        setEmailStatus(null);
        resetOtpEmailState();
        lastCheckedEmailRef.current = '';

        // Reset form data back to original values
        if (selectedDoctor) {
            setFormData({
                name: selectedDoctor.name || '',
                surname: selectedDoctor.surname || '',
                gender: selectedDoctor.gender || '',
                dob: selectedDoctor.dob ? selectedDoctor.dob.split('T')[0] : '',
                email: selectedDoctor.email || '',
                phone: selectedDoctor.phone || '',
                altPhone: selectedDoctor.altPhone || '',
                licenseNumber: selectedDoctor.licenseNumber || '',
                description: selectedDoctor.description || '',
                profession: Array.isArray(selectedDoctor.profession) ? selectedDoctor.profession.map(p => p.professionId || p) : (selectedDoctor.profession?.professionId ? [selectedDoctor.profession.professionId] : (selectedDoctor.profession ? [selectedDoctor.profession] : [])),
            });

            setLinks(
                selectedDoctor.websiteLink?.map((link, index) =>
                    typeof link === 'string'
                        ? { id: index + 1, url: link }
                        : { id: index + 1, url: link.url }
                ) || []
            );

            setEducation(
                selectedDoctor.certification?.map((cert, index) => ({
                    id: index + 1,
                    title: cert.title || '',
                    year: cert.date ? new Date(cert.date).getUTCFullYear().toString() : '',
                    location: cert.location || '',
                    institute: cert.institute || '',
                    description: cert.description || '',
                })) || []
            );
        }
    };

    return (
        <div data-testid="doctor-profile-tab">
            {/* Profile Picture Section */}
            <div className="flex justify-center my-6">
                <div className="relative">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300 overflow-hidden">
                        {selectedDoctor?.image ? (
                            <img src={getFileURL(selectedDoctor?.image)} alt={t('doctorprofiletab.profile')} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-gray-500 text-2xl sm:text-3xl font-medium">
                                {formData?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        )}
                    </div>
                    {userRole !== 'patient' && (
                        <button
                            data-testid="edit-profile-image-button"
                            onClick={handleEditClick}
                            className="absolute bottom-0 right-0 bg-[#4ABDBD] text-white p-1.5 rounded-b-full rounded-tr-full hover:bg-[#3AA8A8] transition-colors cursor-pointer"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="profile-image-modal-title"
                    id="profile-image-modal"
                    data-testid="profile-image-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="profile-image-modal-title"
                            >
                                {t('doctorprofiletab.profile_image_update') || 'Profile Image Update'}
                            </h2>
                            <button
                                data-testid="profile-image-modal-close"
                                onClick={closeModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('doctorprofiletab.select_image') || 'Select Image'} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="profile-image-file-input"
                                        id="admin-profile-image-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="admin-profile-image-upload"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('filestab.choose_file')}
                                        </span>
                                        <span className="text-sm text-gray-500 truncate">
                                            {selectedFile ? selectedFile.name : t('filestab.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {imageSrc && (
                                <div className="relative h-64 bg-gray-100 rounded-xl overflow-hidden shadow-inner border border-gray-200 mb-4">
                                    <Cropper
                                        image={imageSrc}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={1}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        onCropComplete={onCropComplete}
                                        cropShape="round"
                                        showGrid={false}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                data-testid="profile-image-cancel-button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            >
                                {t('doctorprofiletab.cancel') || 'Cancel'}
                            </button>

                            {selectedDoctor?.image && (
                                <button
                                    data-testid="profile-image-remove-button"
                                    onClick={handleRemoveImage}
                                    disabled={removeloading}
                                    className={`px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-lg transition-all duration-300
    ${removeloading
                                            ? "bg-red-300 cursor-not-allowed opacity-70"
                                            : "bg-gradient-to-r from-red-400 to-red-600 hover:shadow-xl hover:scale-105"
                                        }`}
                                >
                                    {removeloading ? (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                    ) : (
                                        t('doctorprofiletab.remove') || 'Remove'
                                    )}
                                </button>
                            )}

                            <button
                                data-testid="profile-image-save-button"
                                onClick={handleSubmitCrop}
                                disabled={!isCropping || isUploading}
                                className={`px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-lg transition-all duration-300 ${!isCropping || isUploading
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-xl hover:scale-105'
                                    }`}
                            >
                                {isUploading ? (
                                    <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                ) : (
                                    selectedDoctor?.image
                                        ? t('doctorprofiletab.update')
                                        : t('doctorprofiletab.submit')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Personal Information Section */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="personal-details-heading">
                    {t('doctorprofiletab.heading1')}
                </h2>
                {userRole !== 'patient' && !isEditMode && !isIncompleteMode && (
                    <button
                        onClick={handlePersonalInfoEdit}
                        disabled={isLoadingPersonalInfo}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoadingPersonalInfo ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="edit-personal-info-button"
                    >
                        {isLoadingPersonalInfo ? (
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Edit className="h-5 w-5" />
                        )}
                        <span data-testid="edit-text">{t('doctorprofiletab.edit')}</span>
                    </button>
                )}
            </div>

            {/* Incomplete Profile Alert */}
            {isIncompleteMode && incompleteFields.length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-xl shadow-md" data-testid="incomplete-profile-alert">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-bold text-yellow-800 mb-1">
                                {t('doctorprofiletab.incomplete_profile_title')}
                            </h3>
                            <p className="text-sm text-yellow-700">
                                {t('doctorprofiletab.incomplete_profile_message')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {isEditMode ? (
                <form data-testid="personal-info-form" onSubmit={handleSubmitPersonalInfo} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* 1. Gender */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="gender-label-edit">
                            {t('doctorprofiletab.gender')} {isEditMode && <span className="text-red-500">*</span>}
                        </label>
                        {isEditMode ? (
                            <>
                                <GenderSelect
                                    value={formData.gender}
                                    onChange={(val) => {
                                        setFormData(prev => ({ ...prev, gender: val }));
                                        setErrors(prev => ({ ...prev, gender: null }));
                                    }}
                                    options={getGenderOptions(t)}
                                    placeholder={t('doctorprofiletab.select_gender')}
                                    error={!!errors.gender}
                                />
                                {errors.gender && <p className="text-red-500 text-sm mt-1" data-testid="gender-error">{errors.gender}</p>}
                            </>
                        ) : (
                            <p className="text-gray-800" data-testid="gender-value-edit">{formData.gender ? t(`doctorprofiletab.${formData.gender}`) : '-'}</p>
                        )}
                    </div>
                    {/* 2. First Name */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="name-label-edit">
                            {t('doctorprofiletab.name')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorprofiletab.nametext')}
                            maxLength={25}
                            data-testid="name-input"
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1" data-testid="name-error">{errors.name}</p>}
                        {charLimitReached.name && <p className="text-red-500 text-sm mt-1">{t('doctorprofiletab.reached_text_limit', { limit: 25 })}</p>}
                    </div>
                    {/* 3. Surname */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="surname-label-edit">
                            {t('doctorprofiletab.surname')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="surname"
                            value={formData.surname}
                            onChange={handleInputChange}
                            disabled={userRole !== 'admin' && userRole !== 'Admin' && isIncompleteMode && !incompleteFields.includes('surname')}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 uppercase ${userRole !== 'admin' && userRole !== 'Admin' && isIncompleteMode && !incompleteFields.includes('surname')
                                ? 'bg-gray-100 cursor-not-allowed'
                                : errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'
                                } ${isIncompleteMode && incompleteFields.includes('surname') ? 'border-yellow-400 bg-yellow-50' : ''}`}
                            placeholder={t('doctorprofiletab.surnametext')}
                            maxLength={25}
                            data-testid="surname-input"
                        />
                        {errors.surname && <p className="text-red-500 text-sm mt-1" data-testid="surname-error">{errors.surname}</p>}
                        {charLimitReached.surname && <p className="text-red-500 text-sm mt-1">{t('doctorprofiletab.reached_text_limit', { limit: 25 })}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="dob-label-edit">
                            {t('doctorprofiletab.dob')} {isEditMode && <span className="text-red-500">*</span>}
                        </label>
                        {isEditMode ? (
                            <>
                                <input
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleInputChange}
                                    min="1900-01-01"
                                    max={(() => {
                                        const d = new Date();
                                        if (isIncompleteMode) d.setFullYear(d.getFullYear() - 18);
                                        return d.toISOString().split('T')[0];
                                    })()}
                                    className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${(isIncompleteMode && incompleteFields.includes('dob')) ? 'border-yellow-400 bg-yellow-50' : ''} ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                    data-testid="dob-input"
                                />
                                {errors.dob && <p className="text-red-500 text-sm mt-1" data-testid="dob-error">{errors.dob}</p>}
                            </>
                        ) : (
                            <p className="text-gray-800" data-testid="dob-value-edit">{formatDobDisplay(formData.dob)}</p>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="email-label-edit">
                            {t('doctorprofiletab.email')} {(userRole === 'admin' || userRole === 'Admin') && <span className="text-red-500">*</span>}
                        </label>
                        {(userRole === 'admin' || userRole === 'Admin') ? (
                            <>
                                <div className="relative">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            handleInputChange(e);
                                            if (e.target.value !== formData.email) {
                                                setEmailStatus(null);
                                                resetOtpEmailState();
                                            }
                                        }}
                                        disabled={emailOtpVerified && (selectedDoctor?.email || doctor?.email) !== formData.email}
                                        maxLength={254}
                                        className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.email ? 'border-red-500 bg-red-50' : emailStatus === false ? 'border-red-400 focus:border-red-400' : emailStatus === true && emailOtpVerified ? 'border-green-400 focus:border-green-400' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                        data-testid="email-input"
                                    />
                                    {formData.email && emailRegex.test(formData.email.trim()) && (selectedDoctor?.email || doctor?.email) !== formData.email && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {emailCheckLoading ? (
                                                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                            ) : emailStatus === true ? (
                                                emailOtpVerified ? (
                                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                                ) : null
                                            ) : emailStatus === false ? (
                                                <X className="h-5 w-5 text-red-500" />
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                                {emailStatus === false && !errors.email && (
                                    <p className="text-red-500 text-sm mt-1">{t('doctorProfile.email_already_exists')}</p>
                                )}
                                {errors.email && <p className="text-red-500 text-sm mt-1" data-testid="email-error">{errors.email}</p>}

                                {/* Email OTP Verification UI */}
                                {formData.email && emailStatus === true && (selectedDoctor?.email || doctor?.email) !== formData.email && !emailOtpVerified && (
                                    <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                        <div className="flex justify-between items-center px-1">
                                            <span className="text-sm font-medium text-gray-700">{t('doctorProfile.email_verification')}</span>
                                            {!emailOtpSent ? (
                                                <button
                                                    type="button"
                                                    onClick={handleSendEmailOtp}
                                                    disabled={loading || sendOtpLoading}
                                                    className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                                    data-testid="doctor-profile-send-email-otp-button"
                                                >
                                                    <Send className="w-3.5 h-3.5" />
                                                    {t('doctorProfile.send_otp')}
                                                </button>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    {emailOtpTimer > 0 ? (
                                                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                            00:{emailOtpTimer < 10 ? `0${emailOtpTimer}` : emailOtpTimer}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSendEmailOtp}
                                                            disabled={loading || sendOtpLoading}
                                                            className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                            data-testid="doctor-profile-resend-email-otp-button"
                                                        >
                                                            <RefreshCw className="w-3.5 h-3.5" />
                                                            {t('doctorProfile.resend')}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {emailOtpSent && (
                                            <div className="relative flex items-center gap-2 mt-1">
                                                <input
                                                    type="text"
                                                    maxLength={6}
                                                    value={emailOtpCode}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, '');
                                                        setEmailOtpCode(val);
                                                        if (val.length === 6) {
                                                            handleVerifyEmailOtp(val);
                                                        }
                                                    }}
                                                    placeholder={t('doctorProfile.enter_6_digit_otp')}
                                                    className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${emailOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                                    disabled={emailOtpVerifyingRef.current}
                                                    data-testid="doctor-profile-email-otp-input"
                                                />
                                                {emailOtpVerifyingRef.current && (
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {emailOtpError && (
                                            <p className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                                <X className="w-3 h-3" />
                                                {emailOtpError}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {emailOtpVerified && (selectedDoctor?.email || doctor?.email) !== formData.email && (
                                    <p className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                        <CheckCircle className="w-4 h-4" /> {t('doctorProfile.email_verified')}
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-gray-800" data-testid="email-value-edit">{formData.email}</p>
                                {userRole !== 'patient' && ((doctor?.emailVerified || selectedDoctor?.emailVerified) ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                        <CheckCircle className="w-3 h-3" /> {t('doctorprofiletab.verified', 'Verified')}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                        <XCircle className="w-3 h-3" /> {t('doctorprofiletab.not_verified', 'Not Verified')}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="phone-label-edit">
                            {t('doctorprofiletab.phone')} {(isIncompleteMode && incompleteFields.includes('phone') || userRole === 'admin' || userRole === 'Admin') && <span className="text-red-500">*</span>}
                        </label>
                        {(isIncompleteMode && incompleteFields.includes('phone') || userRole === 'admin' || userRole === 'Admin') ? (
                            <>
                                <div className="relative">
                                    <div className={`flex items-center border rounded-xl p-2 transition-all duration-300 ${(isIncompleteMode && incompleteFields.includes('phone')) ? 'border-yellow-400 bg-yellow-50' : ''} ${errors.phone ? 'border-red-500 bg-red-50' : phoneStatus === false ? 'border-red-400 focus-within:border-red-400' : phoneStatus === true && phoneOtpVerified ? 'border-green-400 focus-within:border-green-400' : 'border-gray-300 hover:border-[#4ABDBD] focus-within:border-[#4ABDBD]'}`}>
                                        <PhoneCountryDropdown
                                            countryTimezoneList={countryTimezoneList}
                                            countryId={formData.countryId}
                                            onSelect={({ countryId, timeZone, countryName }) => {
                                                setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                                setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                                setPhoneStatus(null);
                                                resetOtpPhoneState();
                                            }}
                                            disabled={phoneOtpVerified}
                                        />
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                handleInputChange(e);
                                                // Reset OTP states if user modifies phone manually
                                                if (e.target.value.replace(/[^0-9]/g, '') !== formData.phone) {
                                                    setPhoneStatus(null);
                                                    resetOtpPhoneState();
                                                }
                                            }}
                                            maxLength={selectedCountry?.mobileMaxLength || 10}
                                            className="w-full p-2 focus:outline-none text-sm bg-transparent"
                                            placeholder={t('doctorprofiletab.phonetext')}
                                            data-testid="phone-input"
                                        />
                                    </div>
                                    {formData.phone && /^\d+$/.test(formData.phone) && formData.phone.length >= (selectedCountry?.mobileMinLength || 9) && formData.phone.length <= (selectedCountry?.mobileMaxLength || 10) && phoneStatus !== null && (selectedDoctor?.phone || doctor?.phone) !== formData.phone && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {mobileCheckLoading ? (
                                                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                            ) : phoneStatus ? (
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                            ) : (
                                                <X className="h-5 w-5 text-red-500" />
                                            )}
                                        </div>
                                    )}
                                </div>
                                {phoneStatus === false && !errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">{t('doctorProfile.phone_already_exists')}</p>
                                )}
                                {errors.phone && <p className="text-red-500 text-sm mt-1" data-testid="phone-error">{errors.phone}</p>}

                                {/* OTP Verification UI */}
                                {formData.phone && phoneStatus === true && (selectedDoctor?.phone || doctor?.phone) !== formData.phone && !phoneOtpVerified && (
                                    <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                        <div className="flex justify-between items-center px-1">
                                            <span className="text-sm font-medium text-gray-700">{t('doctorProfile.phone_verification')}</span>
                                            {!phoneOtpSent ? (
                                                <button
                                                    type="button"
                                                    onClick={handleSendPhoneOtp}
                                                    disabled={loading}
                                                    className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                                    data-testid="doctor-profile-send-phone-otp-button"
                                                >
                                                    <Send className="w-3.5 h-3.5" />
                                                    {t('doctorProfile.send_otp')}
                                                </button>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    {phoneOtpTimer > 0 ? (
                                                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                            00:{phoneOtpTimer < 10 ? `0${phoneOtpTimer}` : phoneOtpTimer}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSendPhoneOtp}
                                                            disabled={loading}
                                                            className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                            data-testid="doctor-profile-resend-phone-otp-button"
                                                        >
                                                            <RefreshCw className="w-3.5 h-3.5" />
                                                            {t('doctorProfile.resend')}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {phoneOtpSent && (
                                            <div className="relative flex items-center gap-2 mt-1">
                                                <input
                                                    type="text"
                                                    maxLength={6}
                                                    value={phoneOtpCode}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/\D/g, '');
                                                        setPhoneOtpCode(val);
                                                        if (val.length === 6) {
                                                            handleVerifyPhoneOtp(val);
                                                        }
                                                    }}
                                                    placeholder={t('doctorProfile.enter_6_digit_otp')}
                                                    className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${phoneOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                                    disabled={phoneOtpVerifyingRef.current}
                                                    data-testid="doctor-profile-phone-otp-input"
                                                />
                                                {phoneOtpVerifyingRef.current && (
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {phoneOtpError && (
                                            <p className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                                <X className="w-3 h-3" />
                                                {phoneOtpError}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {phoneOtpVerified && (selectedDoctor?.phone || doctor?.phone) !== formData.phone && (
                                    <p className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                        <CheckCircle className="w-4 h-4" /> {t('doctorProfile.phone_verified')}
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-gray-800" data-testid="phone-value-edit">{formData.phone || '-'}</p>
                                {userRole !== 'patient' && ((doctor?.phoneNoVerified || selectedDoctor?.phoneNoVerified) ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                        <CheckCircle className="w-3 h-3" /> {t('doctorprofiletab.verified', 'Verified')}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                        <XCircle className="w-3 h-3" /> {t('doctorprofiletab.not_verified', 'Not Verified')}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="alt-phone-label-edit">
                            {t('doctorprofiletab.alt_phone', 'Alternate mobile no')}
                        </label>
                        <input
                            type="tel"
                            name="altPhone"
                            value={formData.altPhone}
                            onChange={handleInputChange}
                            maxLength={selectedCountry?.mobileMaxLength || 10}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorprofiletab.phonetext')}
                            data-testid="alt-phone-input"
                        />
                        {errors.altPhone && <p className="text-red-500 text-sm mt-1" data-testid="alt-phone-error">{errors.altPhone}</p>}
                    </div>
                    {/* Profession Select */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1">
                            {t('doctorprofiletab.profession', 'Profession')} <span className="text-red-500">*</span>
                        </label>
                        <ProfessionSelect
                            value={formData.profession}
                            onChange={(val) => {
                                setFormData(prev => ({ ...prev, profession: val }));
                                if (errors.profession) setErrors(prev => ({ ...prev, profession: '' }));
                            }}
                            professions={professionListAll || []}
                            loading={professionListAllLoading}
                            placeholder={t('doctorprofiletab.select_profession', 'Select Profession')}
                            error={!!errors.profession}
                        />
                        {errors.profession && <p className="text-red-500 text-sm mt-1">{errors.profession}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="license-label-edit">
                            {t('doctorprofiletab.license_no')}
                        </label>
                        <input
                            type="text"
                            name="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleInputChange}
                            maxLength={20}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.licenseNumber ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorprofiletab.licensetext')}
                            data-testid="license-input"
                        />
                        {errors.licenseNumber && <p className="text-red-500 text-sm mt-1" data-testid="license-error">{errors.licenseNumber}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="country-label-edit">
                            {t('doctorprofiletab.country', 'Country')}
                        </label>
                        {userRole === 'admin' || userRole === 'Admin' ? (
                            <CountryTimeZoneSelect
                                countryTimezoneList={countryTimezoneList}
                                countryId={formData.countryId}
                                timeZone={formData.timeZone}
                                onChange={({ countryId, timeZone }) => {
                                    setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', altPhone: '' }));
                                    if (errors.countryId) setErrors(prev => ({ ...prev, countryId: '', timeZone: '' }));
                                }}
                                error={!!errors.countryId}
                            />
                        ) : (
                            <div className="flex items-center gap-2">
                                <CountryFlag countryName={getCountryName(selectedDoctor?.country)} />
                                <p className="text-gray-800" data-testid="country-value-edit">{getCountryName(selectedDoctor?.country) || '-'}</p>
                            </div>
                        )}
                    </div>
                    <div className="mb-4 sm:col-span-2">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="description-label-edit">
                            {t('doctorprofiletab.description')}
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            // disabled={userRole !== 'admin' && userRole !== 'Admin' && isIncompleteMode && !incompleteFields.includes('description')}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'
                                } ${isIncompleteMode && incompleteFields.includes('description') ? 'border-yellow-400 bg-yellow-50' : ''}`}
                            placeholder={t('doctorprofiletab.descriptiontext')}
                            rows="4"
                            maxLength={100}
                            data-testid="description-textarea"
                        />
                        {errors.description && <p className="text-red-500 text-sm mt-1" data-testid="description-error">{errors.description}</p>}
                        {charLimitReached.description && <p className="text-red-500 text-sm mt-1">{t('doctorprofiletab.reached_text_limit', { limit: 100 })}</p>}
                    </div>
                    {userRole !== 'patient' && (
                        <div className="sm:col-span-2 flex justify-end gap-4">
                            {!isIncompleteMode && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    disabled={isLoadingPersonalInfo}
                                    className={`px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 ${isLoadingPersonalInfo ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    data-testid="cancel-personal-info-button"
                                >
                                    {t('doctorprofiletab.cancel')}
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={isLoadingPersonalInfo || (isIncompleteMode && !areAllRequiredFieldsFilled())}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoadingPersonalInfo || (isIncompleteMode && !areAllRequiredFieldsFilled())
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:scale-105 cursor-pointer'
                                    }`}
                                data-testid="save-personal-info-button"
                            >
                                {isLoadingPersonalInfo ? (
                                    <svg
                                        className="animate-spin h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                ) : (
                                    t('doctorprofiletab.savechanges')
                                )}
                            </button>
                        </div>
                    )}
                </form>
            ) : (
                <div data-testid="personal-info-display" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="name-label">
                            {t('doctorprofiletab.name')}
                        </label>
                        <p className="text-gray-800" data-testid="name-value">{formData.name}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="surname-label">
                            {t('doctorprofiletab.surname')}
                        </label>
                        <p className="text-gray-800" data-testid="surname-value">{formData.surname}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="gender-label">
                            {t('doctorprofiletab.gender')}
                        </label>
                        <p className="text-gray-800" data-testid="gender-value">{t(`doctorprofiletab.${formData.gender}`)}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="dob-label">
                            {t('doctorprofiletab.dob')}
                        </label>
                        <p className="text-gray-800" data-testid="dob-value">{formatDobDisplay(formData.dob)}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="email-label">
                            {t('doctorprofiletab.email')}
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-gray-800" data-testid="email-value">{formData.email}</p>
                            {userRole !== 'patient' && ((doctor?.emailVerified || selectedDoctor?.emailVerified) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> {t('doctorprofiletab.verified', 'Verified')}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                    <XCircle className="w-3 h-3" /> {t('doctorprofiletab.not_verified', 'Not Verified')}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="phone-label">
                            {t('doctorprofiletab.phone')}
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-gray-800" data-testid="phone-value">{formData.phone}</p>
                            {userRole !== 'patient' && ((doctor?.phoneNoVerified || selectedDoctor?.phoneNoVerified) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> {t('doctorprofiletab.verified', 'Verified')}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                    <XCircle className="w-3 h-3" /> {t('doctorprofiletab.not_verified', 'Not Verified')}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="alt-phone-label">
                            {t('doctorprofiletab.alt_phone', 'Alternate mobile no')}
                        </label>
                        <p className="text-gray-800" data-testid="alt-phone-value">{formData.altPhone || '-'}</p>
                    </div>
                    {/* Profession Display */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1">
                            {t('doctorprofiletab.profession', 'Profession')}
                        </label>
                        <div className="flex flex-wrap gap-1">
                            {Array.isArray(selectedDoctor?.profession)
                                ? selectedDoctor.profession.map((p, i) => (
                                    <span key={i} className="text-[#4ABDBD] text-xs font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full">
                                        <ProfessionName profession={p} professionListAll={professionListAll} />
                                    </span>
                                ))
                                : selectedDoctor?.profession?.professionName
                                    ? <span className="text-[#4ABDBD] text-xs font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full"><ProfessionName profession={selectedDoctor.profession} professionListAll={professionListAll} /></span>
                                    : <span className="text-gray-800">-</span>
                            }
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="license-label">
                            {t('doctorprofiletab.license_no')}
                        </label>
                        <p className="text-gray-800" data-testid="license-value">{formData.licenseNumber || '-'}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="country-label">
                            {t('doctorprofiletab.country', 'Country')}
                        </label>
                        <div className="flex items-center gap-2">
                            <CountryFlag countryName={getCountryName(selectedDoctor?.country)} />
                            <p className="text-gray-800" data-testid="country-value">{getCountryName(selectedDoctor?.country) || '-'}</p>
                        </div>
                    </div>
                    <div className="mb-4 sm:col-span-2">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="description-label">
                            {t('doctorprofiletab.description')}
                        </label>
                        <p className="text-gray-800" data-testid="description-value">{formData.description}</p>
                    </div>
                </div>
            )}

            {/* Website Links Section */}
            <div className="border border-gray-300 h-[1px] my-4"></div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="professional-links-heading">
                    {t('doctorprofiletab.heading2')}
                </h2>
                {userRole !== 'patient' && (
                    <button
                        onClick={() => {
                            setIsLinkModalOpen(true);
                            setIsLinkModalEdit(false);
                            setNewLink({ url: '' });
                        }}
                        disabled={isLoadingLink || isEditMode}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoadingLink || isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="add-link-button"
                    >
                        {isLoadingLink ? (
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Plus className="h-5 w-5" />
                        )}
                        <span data-testid="add-link-text">{t('doctorprofiletab.add_link')}</span>
                    </button>
                )}
            </div>
            <div data-testid="links-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {links.length > 0 ? (
                    links.map((link) => (
                        <div
                            key={link.id}
                            className="group relative flex items-center gap-4 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-4 hover:scale-105"
                            data-testid={`link-item-${link.id}`}
                        >
                            {/* Icon Box */}
                            <div className="flex-shrink-0 bg-gradient-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-3 rounded-xl shadow-sm">
                                <Link2 className="w-6 h-6 text-[#4ABDBD]" />
                            </div>

                            {/* Link Info */}
                            <div className="flex-1 min-w-0">
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors duration-300 truncate block"
                                    data-testid={`link-url-${link.id}`}
                                >
                                    {link.url}
                                </a>
                            </div>

                            {/* Action Buttons */}
                            {userRole !== 'patient' && !isEditMode && (
                                <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-all duration-300">
                                    <button
                                        onClick={() => {
                                            setNewLink({ ...link });
                                            setIsLinkModalOpen(true);
                                            setIsLinkModalEdit(true);
                                        }}
                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        data-testid={`edit-link-button-${link.id}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteLink(link.id)}
                                        className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        data-testid={`delete-link-button-${link.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))
                )
                    : (
                        <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8" data-testid="empty-links">
                            <img
                                src={emptylink}
                                alt={t('doctorprofiletab.no_links_found')}
                                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-52 lg:h-52 object-contain"
                            />
                            <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                                {t('doctorprofiletab.emptylink')}
                            </p>
                        </div>
                    )}
            </div>
            {isLinkModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="link-modal-title"
                    id="link-modal"
                    data-testid="link-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="link-modal-title"
                                data-testid="link-modal-title"
                            >
                                {isLinkModalEdit ? t('doctorprofiletab.editlink') : t('doctorprofiletab.addlink')}
                            </h2>
                            <button
                                id="link-modal-close-button"
                                onClick={() => {
                                    setIsLinkModalOpen(false);
                                    setIsLinkModalEdit(false);
                                    setNewLink({ url: '' });
                                    setErrors({});
                                }}
                                aria-label={t('doctorprofiletab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button-link"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <div className="mb-4">
                                <label
                                    className="block text-sm font-semibold text-gray-700 mb-2"
                                    htmlFor="link-url-input"
                                    id="link-url-label"
                                    data-testid="url-label"
                                >
                                    {t('doctorprofiletab.url')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    id="link-url-input"
                                    name="url"
                                    value={newLink.url}
                                    onChange={handleLinkInputChange}
                                    className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.url ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                    placeholder={t('doctorprofiletab.urltext')}
                                    data-testid="url-input"
                                />
                                {errors.url && (
                                    <p
                                        className="mt-2 text-xs text-red-500 font-medium"
                                        id="link-url-error"
                                        data-testid="url-error"
                                    >
                                        {errors.url}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="link-form-cancel-button"
                                onClick={() => {
                                    setIsLinkModalOpen(false);
                                    setIsLinkModalEdit(false);
                                    setNewLink({ url: '' });
                                    setErrors({});
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isLoadingLink}
                                data-testid="cancel-link-button"
                            >
                                {t('doctorprofiletab.cancel')}
                            </button>
                            <button
                                type="button"
                                id="link-form-submit-button"
                                onClick={handleAddOrUpdateLink}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isLoadingLink ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isLoadingLink}
                                data-testid="save-link-button"
                            >
                                {isLoadingLink && (
                                    <span data-testid="loading-spinner-link" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isLoadingLink ? t('doctorprofiletab.loading') : t('doctorprofiletab.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Education Section */}
            <div className="border border-gray-300 h-[1px] mb-4 mt-10"></div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="education-heading">
                    {t('doctorprofiletab.heading3')}
                </h2>
                {userRole !== 'patient' && (
                    <button
                        onClick={() => {
                            setIsEducationModalOpen(true);
                            setIsEducationModalEdit(false);
                            setNewEducation({ title: '', year: '', location: '', institute: '', description: '' });
                        }}
                        disabled={isLoadingEducation || isEditMode}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoadingEducation || isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="add-education-button"
                    >
                        {isLoadingEducation ? (
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Plus className="h-5 w-5" />
                        )}
                        <span data-testid="add-education-text">{t('doctorprofiletab.add_education')}</span>
                    </button>
                )}
            </div>
            <div data-testid="education-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {education.length > 0 ? (
                    education.map((edu) => (
                        <div
                            key={edu.id}
                            className="group relative flex items-start gap-4 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-4 hover:scale-105"
                            data-testid={`education-item-${edu.id}`}
                        >
                            {/* Icon Box */}
                            <div className="flex-shrink-0 bg-gradient-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-3 rounded-xl shadow-sm">
                                <Award className="w-6 h-6 text-[#4ABDBD]" />
                            </div>

                            {/* Education Info */}
                            <div className="flex-1 min-w-0 overflow-hidden">
                                <p
                                    className="text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors duration-300 break-words"
                                    data-testid={`education-title-${edu.id}`}
                                >
                                    {edu.title}
                                </p>
                                <p
                                    className="text-xs text-gray-600 mt-1 break-words"
                                    data-testid={`education-year-location-${edu.id}`}
                                >
                                    {edu.year} | {edu.location}
                                </p>
                                <p
                                    className="text-xs text-gray-600 break-words"
                                    data-testid={`education-institute-${edu.id}`}
                                >
                                    {edu.institute}
                                </p>
                                <p
                                    className="text-xs text-gray-600 mt-2 break-words"
                                    data-testid={`education-description-${edu.id}`}
                                >
                                    {edu.description}
                                </p>
                            </div>

                            {/* Action Buttons */}
                            {userRole !== 'patient' && !isEditMode && (
                                <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-all duration-300">
                                    <button
                                        onClick={() => {
                                            setNewEducation({ ...edu });
                                            setIsEducationModalOpen(true);
                                            setIsEducationModalEdit(true);
                                        }}
                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        data-testid={`edit-education-button-${edu.id}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteEducation(edu.id)}
                                        className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        data-testid={`delete-education-button-${edu.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))
                )
                    : (
                        <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8" data-testid="empty-education">
                            <img
                                src={emptyedu}
                                alt={t('doctorprofiletab.no_education_found')}
                                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 object-contain"
                            />
                            <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4 sm:mt-6">
                                {t('doctorprofiletab.emptyedu')}
                            </p>
                        </div>
                    )}
            </div>
            {isEducationModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="education-modal-title"
                    id="education-modal"
                    data-testid="education-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="education-modal-title"
                                data-testid="education-modal-title"
                            >
                                {isEducationModalEdit ? t('doctorprofiletab.editedu') : t('doctorprofiletab.addedu')}
                            </h2>
                            <button
                                id="education-modal-close-button"
                                onClick={() => {
                                    setIsEducationModalOpen(false);
                                    setIsEducationModalEdit(false);
                                    setNewEducation({ title: '', year: '', location: '', institute: '', description: '' });
                                    setErrors({});
                                }}
                                aria-label={t('doctorprofiletab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button-education"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="education-title-input"
                                        id="education-title-label"
                                        data-testid="education-title-label"
                                    >
                                        {t('doctorprofiletab.title')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="education-title-input"
                                        name="title"
                                        value={newEducation.title}
                                        onChange={handleEducationInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.title ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        placeholder={t('doctorprofiletab.degreetext')}
                                        maxLength={100}
                                        data-testid="education-title-input"
                                    />
                                    {errors.title && (
                                        <p
                                            className="mt-2 text-xs text-red-500 font-medium"
                                            id="education-title-error"
                                            data-testid="education-title-error"
                                        >
                                            {errors.title}
                                        </p>
                                    )}
                                    {charLimitReached.title && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctorprofiletab.reached_text_limit', { limit: 100 })}</p>}
                                </div>
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="education-year-select"
                                        id="education-year-label"
                                        data-testid="education-year-label"
                                    >
                                        {t('doctorprofiletab.year')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="education-year-select"
                                        name="year"
                                        value={newEducation.year}
                                        onChange={handleEducationInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.year ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        data-testid="education-year-select"
                                    >
                                        <option value="">{t('doctorprofiletab.selectyear')}</option>
                                        {years.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.year && (
                                        <p
                                            className="mt-2 text-xs text-red-500 font-medium"
                                            id="education-year-error"
                                            data-testid="education-year-error"
                                        >
                                            {errors.year}
                                        </p>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="education-location-input"
                                        id="education-location-label"
                                        data-testid="education-location-label"
                                    >
                                        {t('doctorprofiletab.location')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="education-location-input"
                                        name="location"
                                        value={newEducation.location}
                                        onChange={handleEducationInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.location ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        placeholder={t('doctorprofiletab.locationtext')}
                                        maxLength={100}
                                        data-testid="education-location-input"
                                    />
                                    {errors.location && (
                                        <p
                                            className="mt-2 text-xs text-red-500 font-medium"
                                            id="education-location-error"
                                            data-testid="education-location-error"
                                        >
                                            {errors.location}
                                        </p>
                                    )}
                                    {charLimitReached.location && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctorprofiletab.reached_text_limit', { limit: 100 })}</p>}
                                </div>
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="education-institute-input"
                                        id="education-institute-label"
                                        data-testid="education-institute-label"
                                    >
                                        {t('doctorprofiletab.institute')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="education-institute-input"
                                        name="institute"
                                        value={newEducation.institute}
                                        onChange={handleEducationInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.institute ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        placeholder={t('doctorprofiletab.institutetext')}
                                        maxLength={100}
                                        data-testid="education-institute-input"
                                    />
                                    {errors.institute && (
                                        <p
                                            className="mt-2 text-xs text-red-500 font-medium"
                                            id="education-institute-error"
                                            data-testid="education-institute-error"
                                        >
                                            {errors.institute}
                                        </p>
                                    )}
                                    {charLimitReached.institute && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctorprofiletab.reached_text_limit', { limit: 100 })}</p>}
                                </div>
                                <div className="mb-4 sm:col-span-2">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="education-description-textarea"
                                        id="education-description-label"
                                        data-testid="education-description-label"
                                    >
                                        {t('doctorprofiletab.description')}
                                    </label>
                                    <textarea
                                        id="education-description-textarea"
                                        name="description"
                                        value={newEducation.description}
                                        onChange={handleEducationInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                                            }`}
                                        placeholder={t('doctorprofiletab.descriptiontext')}
                                        rows="4"
                                        maxLength={150}
                                        data-testid="education-description-textarea"
                                    />
                                    {errors.description && (
                                        <p
                                            className="mt-2 text-xs text-red-500 font-medium"
                                            id="education-description-error"
                                            data-testid="education-description-error"
                                        >
                                            {errors.description}
                                        </p>
                                    )}
                                    {charLimitReached.description && <p className="mt-2 text-xs text-red-500 font-medium">{t('doctorprofiletab.reached_text_limit', { limit: 150 })}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="education-form-cancel-button"
                                onClick={() => {
                                    setIsEducationModalOpen(false);
                                    setIsEducationModalEdit(false);
                                    setNewEducation({ title: '', year: '', location: '', institute: '', description: '' });
                                    setErrors({});
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isLoadingEducation}
                                data-testid="cancel-education-button"
                            >
                                {t('doctorprofiletab.cancel')}
                            </button>
                            <button
                                type="button"
                                id="education-form-submit-button"
                                onClick={handleAddOrUpdateEducation}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isLoadingEducation ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'
                                    }`}
                                disabled={isLoadingEducation}
                                data-testid="save-education-button"
                            >
                                {isLoadingEducation && (
                                    <span data-testid="loading-spinner-education" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isLoadingEducation ? t('doctorprofiletab.loading') : t('doctorprofiletab.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorProfileTab;